import { Card, EmptyState, Input } from '@ui/themed';
import { t } from 'i18next';
import { useEffect, useMemo, useState } from 'react';
import { SparkSuspense } from '../../../components/spark-suspense';
import { useDebouncedState } from '../../../hooks/use-debounced-state';
import { useSearchCompanies } from '../hooks/use-companies';
import type { CompanyListQuery } from '../types/api';
import { CompaniesTable } from './companies-table';

const MIN_SEARCH_LENGTH = 2;

export function CompaniesSearchTable(): JSX.Element {
  const [searchValue, setSearchValue] = useState('');

  const searchQuery = useMemo<CompanyListQuery>(
    () => (searchValue ? { search_term: searchValue } : {}),
    [searchValue],
  );

  const {
    data,
    hasNextPage,
    hasPreviousPage,
    nextPage,
    previousPage,
    refetch,
    isError,
    isLoading,
    isFetching,
    isPlaceholderData,
    page,
    pagination,
  } = useSearchCompanies(searchQuery);

  const companies = data ?? [];

  const paginationCounts =
    pagination?.per_page && companies.length
      ? {
          first: pagination.per_page * (page - 1) + 1,
          last: pagination.per_page * (page - 1) + companies.length,
          total: pagination.total,
        }
      : undefined;

  const isSearching = searchValue.length > 0;
  const showEmptyState = companies.length === 0 && !isLoading;
  const blockPaging = isPlaceholderData || isFetching;

  const emptyStateKey = isSearching ? 'no_results_state' : 'empty_state';

  return (
    <SparkSuspense
      onReload={refetch}
      isError={isError}
      errorMessage={t('features:companies.pages.companies_list_page.load_error')}
      isLoading={isLoading}
    >
      {(searchValue.length > 0 || companies.length > 0) && (
        <CompaniesSearchBar onSearchChange={setSearchValue} />
      )}
      {showEmptyState ? (
        <Card>
          <EmptyState
            title={t(`features:companies.pages.companies_list_page.${emptyStateKey}.title`)}
            subtitle={t(`features:companies.pages.companies_list_page.${emptyStateKey}.subtitle`)}
            imageType="customerGroup"
          />
        </Card>
      ) : (
        <CompaniesTable
          companies={companies}
          pagination={{
            hasNext: !blockPaging && hasNextPage,
            hasPrevious: !blockPaging && hasPreviousPage,
            onNext: nextPage,
            onPrevious: previousPage,
            counts: paginationCounts,
          }}
        />
      )}
    </SparkSuspense>
  );
}

function CompaniesSearchBar({
  onSearchChange,
}: {
  onSearchChange: (value: string) => void;
}): JSX.Element {
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearchValue = useDebouncedState(searchValue, 300);

  useEffect(() => {
    // The API requires at least 2 characters to search; a value shorter than MIN_SEARCH_LENGTH clears the filter.
    if (debouncedSearchValue.length >= MIN_SEARCH_LENGTH) {
      onSearchChange(debouncedSearchValue);
    } else {
      onSearchChange('');
    }
  }, [debouncedSearchValue, onSearchChange]);

  return (
    <div className="max-w-3xs">
      <Input
        icon="search"
        placeholder={t('features:companies.pages.companies_list_page.search_placeholder')}
        value={searchValue}
        onChange={setSearchValue}
      />
    </div>
  );
}
